import { createContext } from "preact";
import { useContext, useMemo, useState, useCallback, useEffect } from "preact/hooks";
import axios from "axios";
import { useProjectDetailPage } from "../store";
import { LOCAL_SESSION_ID } from "@shared/components";
import toast from "react-hot-toast";

const isDev = import.meta.env.DEV;
const buildUrl = (p) => (isDev ? `http://localhost:3000${p}` : p);

const DocumentationCardContext = createContext(null);
export const useDocumentationCard = () => {
  const ctx = useContext(DocumentationCardContext);
  if (!ctx) throw new Error("useDocumentationCard debe usarse dentro de <DocumentationCardProvider>");
  return ctx;
};

export const DOCS = [
  { code: "arch-diagram",  field: "archDiagram",  label: "Diagrama de arquitectura" },
  { code: "model-diagram", field: "modelDiagram", label: "Modelo de base de datos" },
  { code: "inf-diagram",   field: "infDiagram",   label: "Diagrama de infraestructura" },
];

const toPublicBaseUrl = (filePath) => buildUrl(`/internal/files/${filePath}`);

export function DocumentationCardProvider({ children }) {
  const { projectId, project, applySessionHeader, role } = useProjectDetailPage();

  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [loadingCode, setLoadingCode] = useState(null);
  const [docSrcMap, setDocSrcMap] = useState({});

  const values = useMemo(() => ({
    "arch-diagram":  project?.archDiagram,
    "model-diagram": project?.modelDiagram,
    "inf-diagram":   project?.infDiagram,
  }), [project]);

  const buildImgUrlWithSession = useCallback((filePath) => {
    if (!filePath) return null;
    const base = toPublicBaseUrl(filePath);
    const sessionId = localStorage.getItem(LOCAL_SESSION_ID) || "";
    const v = project?.updated_at || project?.updatedAt || Date.now();
    const sep = base.includes("?") ? "&" : "?";
    return `${base}${sep}sessionId=${encodeURIComponent(sessionId)}&v=${encodeURIComponent(v)}`;
  }, [project]);

  useEffect(() => {
    const next = {};
    for (const d of DOCS) {
      const fp = values[d.code];
      if (fp) next[d.code] = buildImgUrlWithSession(fp);
    }
    setDocSrcMap(next);
  }, [values, buildImgUrlWithSession]);

  const uploadDocumentation = useCallback(async (code, file) => {
    if (!projectId || !file) return { ok: false, message: "Falta projectId o archivo" };
    setLoadingCode(code);
    applySessionHeader?.();
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      fd.append("projectId", projectId);
      const url = buildUrl(`/${role}/api/projects/documentation/${code}`);
      const res = await axios.post(url, fd, { withCredentials: true, validateStatus: () => true });
      const ok = !!res?.data?.ok;
      const message = res?.data?.message || (ok ? "Documentación actualizada" : "No se pudo actualizar");
      
      if (ok) {
        toast.success(message);
      } else {
        toast.error(message);
      }
      return { ok, message, data: res?.data?.data };
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Error subiendo archivo");
      return { ok: false, message: e?.response?.data?.message || e?.message || "Error subiendo archivo" };
    } finally {
      setLoadingCode(null);
    }
  }, [projectId, role, applySessionHeader]);

  const invalidateDocSrc = useCallback((code) => {
    const fp = values[code];
    setDocSrcMap((prev) => ({
      ...prev,
      [code]: fp ? buildImgUrlWithSession(fp) : undefined,
    }));
  }, [values, buildImgUrlWithSession]);

  const openViewer = useCallback((docDef) => {
    const src = docSrcMap[docDef.code] || null;
    setCurrent({ ...docDef, src });
    setOpen(true);
  }, [docSrcMap]);

  const closeViewer = useCallback(() => { setOpen(false); setCurrent(null); }, []);

  const value = useMemo(() => ({
    project, projectId, role,
    DOCS,
    open, current, openViewer, closeViewer,
    uploadDocumentation,
    loadingCode,
    docSrcMap,
    invalidateDocSrc,
  }), [project, projectId, role, open, current, openViewer, closeViewer, uploadDocumentation, loadingCode, docSrcMap, invalidateDocSrc]);

  return <DocumentationCardContext.Provider value={value}>{children}</DocumentationCardContext.Provider>;
}